import { Skeleton } from '@radix-ui/themes'
import { LuX } from 'react-icons/lu'

import { useTaskProgress } from '../../api'
import { TaskTripDetails } from '../TaskTripDetails'
import styles from './TaskProgressPanel.module.css'
import { TaskProgressPanelProps } from './TaskProgressPanel.types'
import {
  formatAssetId,
  formatProgressPercent,
  getStatusColor,
  getStatusLabel,
} from './TaskProgressPanel.utils'

const SKELETON_ROW_COUNT = 4

// Mirrors TaskTripDetails' row shape (circle + card) so the panel keeps its size while loading.
const TripDetailsSkeleton = () => (
  <div className={styles.skeleton}>
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
      <div className={styles.skeletonRow} key={index}>
        <Skeleton className={styles.skeletonCircle} width="28px" height="28px" />
        <Skeleton className={styles.skeletonCard} height="52px" />
      </div>
    ))}
  </div>
)

/**
 * The task detail content: header (reference, status, progress) plus the stops timeline.
 *
 * Deliberately layout-neutral — it fills whatever box it is given and owns no position, width or
 * height. That lets it serve both the Observatory's floating TaskProgressPopout and the Execution
 * Board's TaskFocusModal right-hand pane from one implementation.
 */
export const TaskProgressPanel = ({
  task,
  showProgress = false,
  tripProgress,
  onClose,
  testId,
}: TaskProgressPanelProps) => {
  const { data: progress, error, isLoading } = useTaskProgress(task.taskDefinition.id)
  const hasError = !!error

  const percent = formatProgressPercent(tripProgress)

  return (
    <div className={styles.panel} data-testid={testId}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.title}>
            Task: {task.taskDefinition.reference ?? task.taskDefinition.id}
          </span>
          {onClose && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              data-testid={`${testId}-close`}
              aria-label="Close"
            >
              <LuX size={16} />
            </button>
          )}
        </div>

        {showProgress && (
          <>
            <span className={styles.assetId}>Asset ID: {formatAssetId(task)}</span>
            <div className={styles.statusRow}>
              <span className={styles.statusBadge} data-testid={`${testId}-status`}>
                <span
                  className={styles.statusDot}
                  style={{ backgroundColor: getStatusColor(task.status) }}
                />
                {getStatusLabel(task.status)}
              </span>
              {percent !== null && (
                <div className={styles.progress} data-testid={`${testId}-progress`}>
                  <span className={styles.progressPercent}>{percent}%</span>
                  <div className={styles.progressTrack}>
                    <div className={styles.progressFill} style={{ width: `${percent}%` }} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.body}>
        {isLoading && (
          <div data-testid={`${testId}-loading`}>
            <TripDetailsSkeleton />
          </div>
        )}

        {!isLoading && hasError && (
          <div className={styles.error} data-testid={`${testId}-error`}>
            Failed to load trip details. Please try again.
          </div>
        )}

        {!isLoading && !hasError && progress && (
          <TaskTripDetails progress={progress} locations={task.taskDefinition.locations} />
        )}
      </div>
    </div>
  )
}
